import { useCallback, useEffect, useRef, useState } from 'react';
import { densitySignature, simulateDensity, type DensityDemand, type DensityResult } from './density';
import { useDensityLegend } from './densityLegend';
import type { Network } from './network';
import type { NetworkDisplay } from './networkDisplay';

export function useDensity(selectionId: string, network: Network, heat: number, buildings: string,
  demands?: DensityDemand[], onCalculated?: () => void) {
  const signature = densitySignature(network, `${buildings}|${JSON.stringify(demands)}`, heat);
  const [results, setResults] = useState<Record<string, DensityResult | undefined>>({});
  const [displays, setDisplays] = useState<Record<string, NetworkDisplay>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { legend, saveLegend } = useDensityLegend();
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const latest = useRef({ selectionId, signature, onCalculated });
  latest.current = { selectionId, signature, onCalculated };
  const cancel = useCallback(() => {
    clearTimeout(timer.current); timer.current = undefined; setLoading(false); setError('');
  }, []);
  // Changed data makes the result obsolete for good (even if the data comes back), without erasing it.
  useEffect(() => {
    cancel();
    setResults((prev) => {
      const current = prev[selectionId];
      return current && !current.stale && current.signature !== signature
        ? { ...prev, [selectionId]: { ...current, stale: true } } : prev;
    });
    return () => clearTimeout(timer.current);
  }, [selectionId, signature, cancel]);
  const result = results[selectionId];
  const stale = !!result && (!!result.stale || result.signature !== signature);
  const display: NetworkDisplay = result && displays[selectionId] === 'density' ? 'density' : 'pipes';
  const setDisplay = (mode: NetworkDisplay) => {
    if (mode === 'density' && !result) return;
    setDisplays((prev) => ({ ...prev, [selectionId]: mode }));
  };
  const canCalculate = network.plants.length > 0 && network.pipes.length > 0;
  const calculate = () => {
    if (!canCalculate || timer.current) return;
    setLoading(true); setError('');
    timer.current = setTimeout(() => {
      timer.current = undefined;
      if (latest.current.selectionId !== selectionId || latest.current.signature !== signature) return;
      try {
        const next = simulateDensity(network, heat, signature, demands);
        setResults((prev) => ({ ...prev, [selectionId]: next }));
        setDisplays((prev) => ({ ...prev, [selectionId]: 'density' }));
        latest.current.onCalculated?.();
      } catch (e: unknown) {
        // A failed run keeps the previous result and its validity untouched.
        setError(e instanceof Error ? e.message : 'Calcul impossible.');
      }
      setLoading(false);
    }, 650);
  };
  return { result, stale, display, setDisplay, loading, error, canCalculate, calculate, cancel, legend, saveLegend };
}
